import type { ApiClient } from "@hakili/api-client";
import { messageOf } from "@hakili/app-shell";
import type {
  CabinetEntriesAnswer,
  CabinetEntryAnswer,
  CabinetEntryLine,
  CabinetVisaAnswer,
} from "@hakili/contracts";
import { formatDate, t } from "@hakili/i18n";
import { CheckCircle2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { EntryForm } from "./entry.js";

/**
 * `round C5-2`, `M3`: **the responsable's desk , « À viser », the return, the
 * visa** (`hakili-expert.jsx`, `BureauRapports`; the founder's word `OD-34`).
 *
 * The desk lists the entries as they stand , « À viser » once the field
 * expert attested, « Renvoyée » with its note, « Visée et envoyée » , reads
 * one, and either returns it with a note (« Renvoyer à l'expert ») or gives
 * the visa: « Les constats saisis sont ceux de mon rapport » ticked, «
 * Viser et envoyer ». The send is the report door the console calls; the
 * verdict is read AFTER (decision B). A one-person cabinet: the
 * responsable's own entry is his to send , one act.
 *
 * `ROUND S4-2-REFERENCE-DOCUMENT`, M1 (1) and (2): the cabinet MAY attach
 * its own report on its own paper, uploaded by the documents' door (owner
 * type `expertiseReport`, the document of record, never altered by Hakili
 * once stored) , the register's RIGHT, never an obligation of this door.
 * Joining it is optional at the visa; Hakili's own dossier (the verdict,
 * derived from this same entry) is what every bank reads, never the
 * cabinet's own pièce, attached or not. A paper (or a correction) may be
 * added later, from « Mes rapports », through « Corriger le rapport ».
 *
 * `M4` adds « Mes rapports », the history with the 90-day validity.
 */

/** One line of the responsable's desk, exactly as `GET /cabinet/entries` sends it (the contract). */
export type DeskEntry = CabinetEntryLine;

const fill = (key: Parameters<typeof t>[0], values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, value), t(key));

export const stateWords = (entry: DeskEntry): { readonly label: string; readonly shade: string } => {
  if (entry.status === "vise")
    return { label: t("expert.bureau.visee"), shade: "bg-emerald-50 text-emerald-700" };
  if (entry.status === "renvoye")
    return { label: t("expert.bureau.renvoyee"), shade: "bg-amber-50 text-amber-700" };
  // Round `C6`: the cabinet's own correction, the entry open again until the visa.
  if (entry.status === "correction")
    return { label: t("expert.bureau.correction"), shade: "bg-amber-50 text-amber-700" };
  if (entry.attestedAt !== null)
    return { label: t("expert.bureau.aViser"), shade: "bg-indigo-50 text-indigo-700" };
  return { label: t("expert.bureau.enCours"), shade: "border border-gray-200 bg-white text-gray-500" };
};

export function DeskReports(props: {
  readonly api: ApiClient;
  readonly uploads?: typeof globalThis.fetch;
  /** `round C5-2`, `M4`: « Mes rapports », the history under the entries in hand. */
  readonly history?: React.ReactNode | ((relire: () => void) => React.ReactNode);
}): React.JSX.Element {
  const { api } = props;
  const uploads = props.uploads ?? globalThis.fetch.bind(globalThis);
  const [entries, setEntries] = useState<readonly DeskEntry[] | null>(null);
  const [opened, setOpened] = useState<DeskEntry | null>(null);
  const [note, setNote] = useState("");
  const [paper, setPaper] = useState<{ documentId: string; name: string } | null>(null);
  const [attested, setAttested] = useState(false);
  const [busy, setBusy] = useState(false);
  const [word, setWord] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [epoch, setEpoch] = useState(0);

  const read = useCallback(async (): Promise<void> => {
    try {
      const answer = await api.request<CabinetEntriesAnswer>("/api/v1/cabinet/entries", "get");
      setEntries(answer.entries);
    } catch (failure) {
      setRefusal(messageOf(failure, t("common.error.connexionEchouee")));
    }
  }, [api]);

  useEffect(() => {
    void read();
  }, [read, epoch]);

  const sendBack = (): void => {
    if (opened === null || busy) return;
    setBusy(true);
    setRefusal(null);
    void (async () => {
      try {
        await api.request<CabinetEntryAnswer>("/api/v1/cabinet/demands/{id}/entry/return", "post", {
          params: { id: opened.demandId },
          body: { note },
        });
        setWord(fill("expert.bureau.renvoyeeMot", { reference: opened.reference }));
        setOpened(null);
        setNote("");
        setEpoch((now) => now + 1);
      } catch (failure) {
        setRefusal(messageOf(failure, t("common.error.connexionEchouee")));
      } finally {
        setBusy(false);
      }
    })();
  };

  const attachPaper = (file: File): void => {
    if (opened === null) return;
    void (async () => {
      try {
        const ticket = await api.request<{ id: string; url: string; headers: Record<string, string> }>(
          "/api/v1/documents/{ownerType}/{ownerId}/upload-ticket",
          "post",
          {
            params: { ownerType: "expertiseReport", ownerId: opened.demandId },
            body: { name: file.name, mimeType: file.type, sizeBytes: file.size, source: "cabinet" },
          },
        );
        const put = await uploads(ticket.url, { method: "PUT", headers: ticket.headers, body: file });
        if (!put.ok) throw new Error(t("common.error.connexionEchouee"));
        const confirmed = await api.request<{ documentId: string }>(
          "/api/v1/documents/{ownerType}/{ownerId}/confirm",
          "post",
          {
            params: { ownerType: "expertiseReport", ownerId: opened.demandId },
            body: { ticketId: ticket.id },
          },
        );
        setPaper({ documentId: confirmed.documentId, name: file.name });
      } catch (failure) {
        setRefusal(messageOf(failure, t("common.error.connexionEchouee")));
      }
    })();
  };

  const visa = (): void => {
    // `ROUND S4-2-REFERENCE-DOCUMENT`, M1 (2): the cabinet's own paper is a
    // right, never an obligation , `paper === null` no longer blocks.
    if (opened === null || busy || !attested) return;
    setBusy(true);
    setRefusal(null);
    void (async () => {
      try {
        await api.request<CabinetVisaAnswer>("/api/v1/cabinet/demands/{id}/entry/visa", "post", {
          params: { id: opened.demandId },
          body: { ...(paper === null ? {} : { documentId: paper.documentId }), attested: true },
        });
        setWord(fill("expert.bureau.viseeMot", { reference: opened.reference }));
        setPaper(null);
        setAttested(false);
        setOpened(null);
        setEpoch((now) => now + 1);
      } catch (failure) {
        setRefusal(messageOf(failure, t("common.error.connexionEchouee")));
      } finally {
        setBusy(false);
      }
    })();
  };

  if (opened !== null) {
    const state = stateWords(opened);
    const canVisa = opened.status !== "vise" && (opened.attestedAt !== null || opened.status === "saisi");
    return (
      <div data-testid="bureau-saisie">
        <div className="px-5 pb-3 pt-5">
          <button
            type="button"
            onClick={() => {
              setOpened(null);
              setPaper(null);
              setAttested(false);
            }}
            className="text-xs text-indigo-700"
            data-testid="bureau-retour"
          >
            {t("expert.bureau.retour")}
          </button>
          <h1 className="mt-1 text-lg font-semibold leading-snug tracking-tight">{opened.vehicleLabel}</h1>
          <p className="text-xs text-gray-500 tabular-nums">
            {opened.reference} · {opened.sellerName} ·{" "}
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${state.shade}`}>
              {state.label}
            </span>
          </p>
          {opened.enteredByName === null ? null : (
            <p className="mt-1 text-xs text-gray-500" data-testid="bureau-saisi-par">
              {fill("expert.bureau.saisiPar", {
                name: opened.enteredByName,
                date: formatDate(new Date(opened.enteredAt)),
              })}
            </p>
          )}
        </div>
        <EntryForm
          api={api}
          demandId={opened.demandId}
          chair="responsable"
          readOnly={opened.status !== "vise" && opened.status !== "correction"}
          uploads={uploads}
        />
        {canVisa ? (
          <div className="space-y-3 px-5 pb-8" data-testid="bureau-visa">
            <div className="rounded-2xl border border-gray-200 bg-white px-4 py-4">
              <h2 className="text-sm font-semibold">{t("expert.bureau.renvoyerTitre")}</h2>
              <textarea
                rows={2}
                value={note}
                onChange={(event) => {
                  setNote(event.target.value);
                }}
                placeholder={t("expert.bureau.renvoyerAide")}
                data-testid="bureau-renvoyer-note"
                className="mt-2 w-full resize-none rounded-xl border border-gray-200 px-3 py-2.5 text-sm"
              />
              <button
                type="button"
                disabled={busy || note.trim() === ""}
                onClick={sendBack}
                data-testid="bureau-renvoyer"
                className="mt-2 w-full rounded-2xl border border-amber-200 bg-amber-50 py-2.5 text-sm font-medium text-amber-700 disabled:opacity-40"
              >
                {t("expert.bureau.renvoyer")}
              </button>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white px-4 py-4">
              <h2 className="text-sm font-semibold">{t("expert.bureau.visaTitre")}</h2>
              <p className="mt-1 text-xs leading-relaxed text-gray-500">{t("expert.bureau.visaAide")}</p>
              <label
                className={`mt-3 block cursor-pointer rounded-xl border px-3 py-2.5 text-center text-xs font-medium ${
                  paper === null
                    ? "border-dashed border-gray-300 text-gray-600"
                    : "border-indigo-200 bg-indigo-50 text-indigo-700"
                }`}
                data-testid="bureau-papier"
                data-joint={paper === null ? "non" : "oui"}
              >
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file !== undefined) attachPaper(file);
                  }}
                />
                {paper === null
                  ? t("expert.bureau.joindrePapier")
                  : fill("expert.bureau.papierJoint", { name: paper.name })}
              </label>
              <button
                type="button"
                onClick={() => {
                  setAttested(!attested);
                }}
                aria-pressed={attested}
                data-testid="bureau-attestation"
                className="mt-3 flex w-full items-start gap-2.5 text-left"
              >
                <span
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 ${
                    attested ? "border-indigo-600 bg-indigo-600" : "border-gray-300"
                  }`}
                >
                  {attested ? <CheckCircle2 className="h-3.5 w-3.5 text-white" aria-hidden="true" /> : null}
                </span>
                <span className="text-xs leading-relaxed text-gray-700">
                  {t("expert.bureau.attestation")}
                </span>
              </button>
              <button
                type="button"
                disabled={busy || !attested}
                onClick={visa}
                data-testid="bureau-viser"
                className="mt-3 w-full rounded-2xl bg-indigo-600 py-3 text-sm font-medium text-white disabled:opacity-40"
              >
                {t("expert.bureau.viser")}
              </button>
              {!attested ? (
                <p className="mt-2 text-center text-xs text-amber-700" data-testid="bureau-visa-garde">
                  {t("expert.bureau.gardeAttestation")}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
        {refusal === null ? null : (
          <p role="alert" className="px-5 pb-6 text-sm text-rose-600" data-testid="bureau-refus">
            {refusal}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="pb-20" data-testid="bureau-rapports">
      <div className="px-5 pb-3 pt-5">
        <h1 className="text-xl font-semibold tracking-tight">{t("expert.bureau.titre")}</h1>
        <p className="mt-1 text-sm text-gray-500">{t("expert.bureau.sousTitre")}</p>
      </div>
      {word === null ? null : (
        <div className="mb-3 px-5">
          <p
            className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-xs text-emerald-700"
            data-testid="bureau-mot"
          >
            {word}
          </p>
        </div>
      )}
      {refusal === null ? null : (
        <p role="alert" className="px-5 pb-3 text-sm text-rose-600" data-testid="bureau-refus">
          {refusal}
        </p>
      )}
      <div className="px-5">
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          {entries === null ? (
            <p className="px-4 py-3 text-xs text-gray-400">{t("common.state.chargement")}</p>
          ) : entries.length === 0 ? (
            <p className="px-4 py-3 text-xs text-gray-400" data-testid="bureau-aucune">
              {t("expert.bureau.aucune")}
            </p>
          ) : (
            <ul className="divide-y divide-gray-50">
              {entries.map((entry) => {
                const state = stateWords(entry);
                return (
                  <li key={entry.demandId}>
                    <button
                      type="button"
                      data-testid={`bureau-saisie-${entry.reference}`}
                      onClick={() => {
                        setOpened(entry);
                        setWord(null);
                      }}
                      className="flex w-full items-start justify-between gap-3 px-4 py-3.5 text-left"
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-gray-900">
                          {entry.vehicleLabel}
                        </span>
                        <span className="block text-xs text-gray-500 tabular-nums">
                          {entry.reference} · {entry.sellerName}
                          {entry.enteredByName === null ? "" : ` · ${entry.enteredByName}`}
                        </span>
                      </span>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${state.shade}`}
                      >
                        {state.label}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
      {typeof props.history === "function"
        ? props.history(() => {
            setEpoch((now) => now + 1);
          })
        : (props.history ?? null)}
    </div>
  );
}
